import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, XCircle, ArrowRight } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import reservationsService from '../services/reservationsService';
import Leads from './Leads';

// "Leads and Reservations" (owner, 2026-08-07): one nav entry, two tabs.
// Lead Management moved here from Marketing — a stalled prospect is future
// revenue to WORK, which is the same job as a reservation hold. The old
// /marketing?view=lead-management and /leads destinations redirect in.
// Leads first (owner, 2026-08-07) — it is also the landing tab.
const PAGE_TABS = [
  { key: 'leads', label: 'Leads' },
  { key: 'reservations', label: 'Reservations' },
];

const STATUS_PILL = {
  active: 'bg-sky-50 text-sky-700 border-sky-100',
  converted: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  expired: 'bg-slate-50 text-slate-500 border-slate-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-100',
};

const ReservationsList = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const pageTab = PAGE_TABS.find((t) => t.key === rawTab) ? rawTab : 'leads';
  const setPageTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'leads') params.delete('tab');
    else params.set('tab', key);
    setSearchParams(params, { replace: true });
  };
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reservationsService.getAll(facilityId, {
        status_filter: filter === 'all' ? undefined : filter,
      });
      setReservations(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load reservations.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side display-only expiry — the scheduler nightly job is the
  // source of truth for actually flipping status to expired.
  const today = new Date().toISOString().slice(0, 10);
  const displayStatus = (r) => {
    if (r.status === 'active' && r.reservation_end && r.reservation_end < today) {
      return 'expired';
    }
    return r.status;
  };

  const cancel = async (r) => {
    if (!window.confirm(`Cancel reservation on unit ${r.unit_number}?`)) return;
    try {
      await reservationsService.cancel(facilityId, r._id);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not cancel.');
    }
  };

  const convertToRental = (r) => {
    // Pre-fill the Rent wizard with customer + unit. The rent flow knows
    // how to read these query params (existing pattern from /rentals/new).
    const params = new URLSearchParams();
    params.set('unit_id', r.unit_id);
    if (r.customer_id) params.set('customer_id', r.customer_id);
    params.set('reservation_id', r._id);
    navigate(`/rentals/new?${params.toString()}`);
  };

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Leads and Reservations</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {pageTab === 'leads'
              ? 'Prospect inbox — every channel (website, phone, AI, walk-in, stalled signups) lands here.'
              : 'Holds on Available units for known customers / leads.'}
          </p>
        </div>
        {pageTab === 'reservations' && (
          <Link
            to="/reservations/new"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" />
            New Reservation
          </Link>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-5">
        {PAGE_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setPageTab(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
              pageTab === t.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {pageTab === 'leads' && <Leads />}

      {pageTab === 'reservations' && (
      <>
      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6">
          {['all', 'active', 'converted', 'expired', 'cancelled'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors capitalize ${
                filter === s
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {s}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reservations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
          <h3 className="text-base font-semibold text-slate-700 mb-1">
            No reservations yet
          </h3>
          <p className="text-[13px] text-slate-500 mb-4">
            Hold an Available unit for a prospect who isn&rsquo;t quite ready to sign.
          </p>
          <Link
            to="/reservations/new"
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700"
          >
            New Reservation
          </Link>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Unit', 'Recipient', 'Start', 'End', 'Fee', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => {
                const ds = displayStatus(r);
                return (
                  <tr key={r._id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2 text-[13px] font-medium text-slate-900">
                      <Link to={`/units/${r.unit_id}`} className="hover:text-blue-600">
                        {r.unit_number || '—'}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-[13px] text-slate-700">
                      <div>{r.recipient_name || '—'}</div>
                      {r.recipient_email && (
                        <div className="text-[11px] text-slate-400">{r.recipient_email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[13px] text-slate-500">{r.reservation_start}</td>
                    <td className="px-3 py-2 text-[13px] text-slate-500">{r.reservation_end}</td>
                    <td className="px-3 py-2 text-[13px] text-slate-700 tabular-nums">
                      ${Number(r.fee_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border capitalize ${
                          STATUS_PILL[ds] || 'bg-slate-50 text-slate-700 border-slate-100'
                        }`}
                      >
                        {ds}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center gap-1">
                        {r.status === 'active' && (
                          <>
                            <button
                              type="button"
                              onClick={() => convertToRental(r)}
                              title="Convert to rental"
                              className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
                            >
                              Convert <ArrowRight className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => cancel(r)}
                              title="Cancel reservation"
                              className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ReservationsList;
